<template>
    <span class="mag-emoji" :class="{ [kind]: true }">
        <mg-icon
            v-if="broken || isRaw"
            icon="file-dashed"
            centered="true"
        />
        <img
            v-else-if="isCustom"
            ref="el"
            class="custom"
            :src="url"
            :alt="alt"
            :title="alt"
            @error="
                () => {
                    broken = true;
                }
            "
            decoding="async"
        />
        <img
            v-else-if="emojiGrapheme && !useOsNativeEmojis"
            ref="el"
            class="twemoji"
            :src="url"
            :alt="alt"
            :title="alt"
            decoding="async"
        />
        <span v-else-if="emojiGrapheme && useOsNativeEmojis" ref="el">{{
            emojiGrapheme
        }}</span>
        <span v-else>{{ emoji }}</span>
    </span>
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent, ref, useTemplateRef } from "vue";
import { getStaticImageUrl } from "@/scripts/get-static-image-url";
import { types } from "magnetar-common";
import {
    magIsCustomEmoji,
    magIsUnicodeEmoji,
    magReactionToLegacy,
} from "@/scripts-mag/mag-util";
import { useSettingsStore } from "@/store/settings-store";
import { storeToRefs } from "pinia";
import { refProp } from "@/composables/reactive";
import { useTooltip } from "@/composables/use-tooltip";
import { twemojiPath } from "@/composables/use-emoji";

import { spawnPopover } from "@/components/layout/popover-layer";

const settingsStore = useSettingsStore();
const { settings } = storeToRefs(settingsStore);
const disableShowingAnimatedImages = refProp(
    settings,
    "disableShowingAnimatedImages",
);
const useOsNativeEmojisStore = refProp(settings, "useOsNativeEmojis");

const el = useTemplateRef("el");

const props = withDefaults(defineProps<{
    emoji: types.Reaction;
    kind?: "small" | "small-expand" | "normal" | "normal-expand" | "large" | "fit";
    isReaction?: boolean;
    hookTooltip?: boolean;
}>(), { kind: "small-expand" });

const broken = ref(false);

const isCustom = computed(() => magIsCustomEmoji(props.emoji));

const isRaw = computed(
    () => !magIsCustomEmoji(props.emoji) && !magIsUnicodeEmoji(props.emoji),
);

const emojiGrapheme = computed(() =>
    magIsUnicodeEmoji(props.emoji) ? (props.emoji as string) : null,
);

const useOsNativeEmojis = computed(
    () => useOsNativeEmojisStore.value && !props.isReaction,
);

const url = computed(() => {
    if (emojiGrapheme.value) {
        return twemojiPath(emojiGrapheme.value);
    } else if (magIsCustomEmoji(props.emoji)) {
        return disableShowingAnimatedImages.value
            ? getStaticImageUrl((props.emoji as types.ReactionShortcode).url)
            : (props.emoji as types.ReactionShortcode).url;
    } else {
        return null;
    }
});

const alt = computed(() => magReactionToLegacy(props.emoji));

if (props.hookTooltip) {
    const XReactionTooltip = defineAsyncComponent(
        () => import("@/components/tooltips/MagReactionTooltip.vue"),
    );

    useTooltip(el, (showing) => {
        if (!el.value) {
            return;
        }

        spawnPopover(
            XReactionTooltip,
            {
                reaction: props.emoji,
                targetElement: new WeakRef(el.value),
            },
            undefined,
            showing,
        );
    });
}
</script>

<style lang="scss" scoped>
.mag-emoji {
    aspect-ratio: 1;
    display: inline-block;
    contain: content;
    font-size: 0;
    vertical-align: middle;
    content-visibility: auto;

    .custom {
        object-fit: contain;
        width: 100%;
        height: 100%;

    }

    .twemoji {
        object-fit: contain;
        width: 100%;
        height: 100%;
    }

    &.small {
        height: 1.5rem;
    }

    &.small-expand {
        height: 1.5rem;
        transition: transform 0.2s ease;

        &:hover {
            transform: scale(1.2);
            isolation: isolate;
        }
    }

    &.normal {
        height: 2.5rem;
    }

    &.normal-expand {
        height: 2.5rem;
        transition: transform 0.2s ease;

        &:hover {
            transform: scale(1.2);
            isolation: isolate;
        }
    }

    &.fit {
        height: 100%;
    }

    &.large {
        height: 60px;
    }
}
</style>
